/**
 * The interface's controller for books shared with the writer (part 38c; decision 75; tables JL, CL, CC): it keeps an
 * invite link's pair when one opens (GP1), claims it as soon as a typed password is at hand (a login, a verify link, the
 * Accept form), opens the keyring at each login, checks the shares on start, when the page is shown or online again and
 * on demand, and drops the account's cache at its logout. Its signals are what Shared with me and the read view show.
 * The account's session reaches the relay; every effect is injected.
 */
import { batch, createSignal } from 'solid-js';
import type { Account, Unlocked } from '../account/account.ts';
import { proveWithSecret } from '../account/actions.ts';
import type { RelayError } from '../account/client.ts';
import type { Typed } from '../account/flows.ts';
import type { KeyringAccess } from '../account/manager.ts';
import type { AccountId } from '../account/wire.ts';
import { readInviteFragment } from '../share/invite-secrets.ts';
import type { Random } from './keyring.ts';
import { manifestOf, Shares } from './shares.ts';
import type { AcceptOutcome, Draft, NoteOutcome } from './shares.ts';
import type { CachedClaim, SharedStore } from './store.ts';
import type { InviteCopy } from '@cockatrice/core';
import { DEFAULT_THEME, metadataFromJson, newMetadata, themeFromJson } from '@cockatrice/layout';
import type { BookMetadata, PdfBook, Theme } from '@cockatrice/layout';
import { bookOf } from './book.ts';
import type { SharedBook } from './book.ts';

/** What the controller runs on. */
export interface SharedOptions {
  readonly account: Account;
  /** Opens the shared store (`openSharedStore(indexedDB)`). */
  readonly openStore: () => Promise<SharedStore>;
  readonly subtle: SubtleCrypto;
  readonly random: Random;
  readonly now: () => number;
}

/** A shared book as its card names it: its title and author (empty when the metadata has none) and its chapters' count. */
export interface Summary {
  readonly title: string;
  readonly author: string;
  readonly chapters: number;
}

/** The shortest time between two checks a page's being shown again starts. */
export const REFRESH_PAUSE_MS = 60_000;

/** What opening an invite link did (JL4, JL7): kept for its claim, incomplete, or accounts off in this build. */
export type LinkTaken = 'kept' | 'incomplete' | 'off';

/** The invite's state as the Accept card shows it: waiting, being claimed, or the last attempt's outcome. */
export type InviteState = { readonly kind: 'none' } | { readonly kind: 'waiting' } | { readonly kind: 'working' } | { readonly kind: 'outcome'; readonly outcome: AcceptOutcome };

/** The controller. */
export class Shared implements KeyringAccess {
  private readonly options: SharedOptions;
  private store: SharedStore | null = null;
  private opening: Promise<SharedStore> | null = null;
  private shares: { readonly account: string; readonly shares: Shares } | null = null;
  private readonly claimsSignal = createSignal<readonly CachedClaim[]>([]);
  private readonly inviteSignal = createSignal<InviteState>({ kind: 'none' });
  private readonly offlineSignal = createSignal(false);
  private readonly unavailableSignal = createSignal<readonly string[]>([]);
  private readonly checkingSignal = createSignal(false);
  private readonly linkSignal = createSignal<LinkTaken | null>(null);
  private readonly summariesSignal = createSignal<ReadonlyMap<string, Summary>>(new Map());
  /** The last check's time on the page's clock, so a page shown again checks at most once a minute. */
  private lastCheck: number | null = null;
  /** CK: the account checked last on this page (a change to another, or a first login, checks again). */
  private checkedFor: string | null = null;
  /** Deep-fix r4: the check last started, so whoever must see its end (a test, nothing on a login's path) can wait. */
  private inFlight: Promise<void> = Promise.resolve();

  constructor(options: SharedOptions) {
    this.options = options;
  }

  /** The claims held for the logged-in account, newest check's. */
  readonly claims = (): readonly CachedClaim[] => this.claimsSignal[0]();

  /** JL: the waiting invite's state. */
  readonly invite = (): InviteState => this.inviteSignal[0]();

  /** CC8: whether the last check couldn't reach the relay (the cache shown as it was). */
  readonly offline = (): boolean => this.offlineSignal[0]();

  /** CC13: the claims whose copy was temporarily unavailable at the last check. */
  readonly unavailable = (): readonly string[] => this.unavailableSignal[0]();

  /** Whether a check is running. */
  readonly checking = (): boolean => this.checkingSignal[0]();

  /** Each claim's book as its card names it, by invite (from its manifest; absent until one is held and opens). */
  readonly summaries = (): ReadonlyMap<string, Summary> => this.summariesSignal[0]();

  /** The claims shown: `claims` kept, and each one's summary read from its manifest. */
  private async show(shares: Shares, claims: readonly CachedClaim[]): Promise<void> {
    const summaries = new Map<string, Summary>();
    for (const claim of claims) {
      const manifest = await shares.manifest(claim.invite);
      if (manifest === null) continue;
      let metadata: BookMetadata | null;
      try {
        metadata = metadataFromJson(manifest.metadata);
      } catch {
        metadata = null;
      }
      summaries.set(claim.invite, { title: metadata?.title ?? '', author: metadata?.author ?? '', chapters: manifest.chapters.length });
    }
    // LO2: an account's books are never shown again once it has logged out (a check that was under way).
    if (shares.closed()) return;
    batch(() => {
      this.claimsSignal[1](claims);
      this.summariesSignal[1](summaries);
    });
  }

  /** JL4, JL7: what the last invite link opened did (said on Shared with me), or null. */
  readonly link = (): LinkTaken | null => this.linkSignal[0]();

  private async opened(): Promise<SharedStore> {
    if (this.store !== null) return this.store;
    this.opening ??= this.options.openStore().then((store) => {
      this.store = store;
      return store;
    });
    return this.opening;
  }

  /**
   * The logged-in account's ID, or null: whose shares are shown, read, checked and written. Signed out by the relay
   * (a revoke, a change of P elsewhere, an expiry, a deletion: C8) is none: deep-fix r4, AS4 as KG6 says (an unlocked
   * device keeps the books only until a logout or a change of P; IN5, IN15's remedies).
   */
  private who(): AccountId | null {
    const status = this.options.account.status();
    return status.kind === 'account' && !status.signedOut ? status.id : null;
  }

  private async sharesFor(account: string): Promise<Shares> {
    if (this.shares?.account === account) return this.shares.shares;
    const { subtle, random, now } = this.options;
    const relay = this.options.account;
    const shares = new Shares({
      account,
      store: await this.opened(),
      subtle,
      random,
      now,
      relay: { get: (path, read) => relay.get(path, read), post: (path, body, read) => relay.post(path, body, read), postForBytes: (path, body, most) => relay.postForBytes(path, body, most) },
      // LO4, LO5: writes stop once another account, or none, is logged in here (this tab's or another's change).
      live: () => this.who() === account,
    });
    this.shares = { account, shares };
    return shares;
  }

  /** The waiting invite and the cached claims read, then a check when logged in (a start of the app: table AS). */
  async start(): Promise<void> {
    try {
      const pending = await (await this.opened()).pending();
      if (pending !== null) this.inviteSignal[1]({ kind: 'waiting' });
      await this.settle();
    } catch {
      // Deep-fix r5: run with nobody waiting (the app's start): storage that refused leaves the page as it was.
    }
  }

  /**
   * Table AS, at a start and at every change of the account's state: nothing while the record isn't read (AS1); the
   * cache of the account this page held, if no longer the logged-in one, closed and cleared (AS4–AS7); every other
   * account's cleared from the store, all of them with nobody logged in (AS2, AS4); the logged-in account's shown and
   * checked once on this page (AS3, CK1–CK3).
   */
  private async settle(): Promise<void> {
    const status = this.options.account.status();
    if (status.kind === 'off' || status.kind === 'loading') return;
    const viewer = this.who();
    const held = this.shares;
    if (held !== null && held.account !== viewer) await this.forget(held.account);
    const store = await this.opened();
    for (const account of await store.accounts()) if (account !== viewer) await store.clear(account);
    if (viewer === null) return;
    if (this.shares?.account !== viewer) {
      const shares = await this.sharesFor(viewer);
      await this.show(shares, await shares.claims());
    }
    const who = this.who();
    if (who !== null && this.checkedFor !== who) await this.check();
  }

  /**
   * GP1, JL1–JL7: an invite link's fragment `hash` read and its pair kept for the claim (replacing one waiting, JL5);
   * the caller clears the address at once. With accounts off nothing is kept.
   */
  async takeLink(hash: string): Promise<LinkTaken> {
    const taken = await this.taking(hash);
    this.linkSignal[1](taken);
    return taken;
  }

  private async taking(hash: string): Promise<LinkTaken> {
    if (!this.options.account.configured()) return 'off';
    const fragment = readInviteFragment(hash);
    if (fragment === null) return 'incomplete';
    await (await this.opened()).keepPending({ claim: fragment.claim, key: fragment.key, at: this.options.now() });
    this.inviteSignal[1]({ kind: 'waiting' });
    return 'kept';
  }

  /**
   * A page shown again or back online: a check, at most once a minute (each costs the relay a list and a keyring read,
   * and a page is shown again often).
   */
  async refresh(): Promise<void> {
    await this.syncPending();
    const now = this.options.now();
    if (this.lastCheck !== null && now - this.lastCheck < REFRESH_PAUSE_MS) return;
    await this.check();
  }

  /** JL: the waiting invite dropped by the writer's choice (Not now on the card). */
  async dismiss(): Promise<void> {
    await (await this.opened()).dropPending();
    this.inviteSignal[1]({ kind: 'none' });
  }

  /**
   * GP3, KG1, JL1–JL3: a typed password admitted `event.account`: the keyring opened and kept, and a waiting invite
   * claimed with a grant from the same secret (an account with F gets the Accept form instead: one more proof).
   */
  async unlocked(event: Unlocked): Promise<void> {
    const shares = await this.sharesFor(event.account);
    await shares.unlock(event.typed);
    const pending = await (await this.opened()).pending();
    const deps = this.options.account.flowDeps();
    if (pending !== null && deps !== null) {
      const proof = await proveWithSecret(deps, event.account, event.typed.secret);
      if (proof.kind === 'ok' && proof.value.kind === 'granted') await this.accepting(shares, event.typed, proof.value.grant);
    }
    // Deep-fix r2, CK2: a login checks the account's shares at once (a new device's, KG2: nothing needed from the
    // writer); r4: started, not waited on, so the login never waits on every copy's download.
    void this.check();
  }

  /** AS8 (KG11, `KeyringAccess`): the logged-in account's keyring opened with `typed` and kept here, then a check. */
  async openKeyring(typed: Typed): Promise<'opened' | 'none' | 'refused' | 'failed'> {
    const account = this.who();
    if (account === null) return 'failed';
    const opened = await (await this.sharesFor(account)).unlock(typed);
    // r4: the sheet says done once the keyring opens; the books arrive with the check it starts.
    if (opened === 'opened') void this.check();
    return opened;
  }

  /** JL3, CL: the waiting invite claimed with `grant`, the password having given `typed` (the Accept form's proof). */
  async accept(grant: string, typed: Typed): Promise<AcceptOutcome> {
    const account = this.who();
    if (account === null) return { kind: 'failed' };
    return this.accepting(await this.sharesFor(account), typed, grant);
  }

  private async accepting(shares: Shares, typed: Typed, grant: string): Promise<AcceptOutcome> {
    const pending = await (await this.opened()).pending();
    if (pending === null) {
      // PW2: another tab claimed it (or put it away): no card, and the shares as they now are.
      this.inviteSignal[1]({ kind: 'none' });
      void this.check();
      return { kind: 'nothing' };
    }
    this.inviteSignal[1]({ kind: 'working' });
    const outcome = await shares.accept(pending, typed, grant);
    this.inviteSignal[1]({ kind: 'outcome', outcome });
    // CL1: the book arrives with the check this starts (r4: not waited on).
    if (outcome.kind === 'accepted') void this.check();
    return outcome;
  }

  /** Whether the waiting invite is still kept (after an outcome, CL2 and CL5 keep it; CL1, CL3 and CL4 don't). */
  async waiting(): Promise<boolean> {
    return (await (await this.opened()).pending()) !== null;
  }

  /** CC1–CC13: the logged-in account's shares checked; the signals follow. */
  check(): Promise<void> {
    const running = this.checkNow();
    this.inFlight = running;
    return running;
  }

  /** Deep-fix r4: the check last started (a login's, an accept's), done or not; resolved at once with none. */
  settled(): Promise<void> {
    return this.inFlight;
  }

  private async checkNow(): Promise<void> {
    const account = this.who();
    if (account === null) return;
    const shares = await this.sharesFor(account);
    this.lastCheck = this.options.now();
    this.checkedFor = account;
    this.checkingSignal[1](true);
    try {
      const outcome = await shares.check();
      batch(() => {
        this.offlineSignal[1](outcome.kind === 'offline');
        this.unavailableSignal[1](outcome.kind === 'checked' ? outcome.unavailable : []);
      });
      await this.show(shares, await shares.claims());
    } catch {
      // Deep-fix r5: a check nobody waits on (a login's, an accept's) never rejects unseen: storage that refused (full,
      // closed at the page's end) leaves the cache and the screen as they were; the next check tries again.
    } finally {
      this.checkingSignal[1](false);
    }
  }

  /** The claim `invite`'s book as the read view shows it (`bookOf` its items); null with no manifest held, or no such claim. */
  async book(invite: string): Promise<SharedBook | null> {
    const manifest = manifestOf(invite);
    return manifest === null ? null : bookOf(await this.open(invite), manifest);
  }

  /** The claim `invite`'s items opened, by item. */
  async open(invite: string): Promise<ReadonlyMap<string, InviteCopy>> {
    const account = this.who();
    return account === null ? new Map() : (await this.sharesFor(account)).open(invite);
  }

  /**
   * Decision 75 (GP6): the claim `invite`'s book as the exports take it (`PdfBook`: its theme, metadata and chapters in
   * the manifest's order) and the chapters' IDs, only when the writer allowed exporting it; null otherwise, or with no manifest held. The
   * setting hides the buttons only (IN16): anyone who can read can copy.
   */
  async exportBook(invite: string, untitled: string): Promise<{ readonly book: PdfBook; readonly ids: readonly string[] } | null> {
    const claim = this.claims().find((each) => each.invite === invite);
    const book = claim?.export === true ? await this.book(invite) : null;
    if (book === null) return null;
    let theme: Theme;
    try {
      // T‴: an invite's copy carries no images, so its theme shows none.
      theme = { ...themeFromJson(book.theme), chapterImage: '' };
    } catch {
      theme = DEFAULT_THEME;
    }
    return {
      book: { id: invite, theme, metadata: book.metadata ?? newMetadata(untitled), chapters: book.chapters.map((chapter) => ({ title: chapter.title, subtitle: '', image: '', text: chapter.text })) },
      ids: book.chapters.map((chapter) => chapter.id),
    };
  }

  /** The PDF's and the EPUB's book of the claim `invite` (`exportBook`'s), or null when it may not or can't be exported. */
  async pdfBook(invite: string, untitled: string): Promise<PdfBook | null> {
    return (await this.exportBook(invite, untitled))?.book ?? null;
  }

  /** The Word export's book of the claim `invite`: its chapters by ID, none pinned to a snapshot here (table PN's `kept` false). */
  async docxBook(invite: string, untitled: string): Promise<{ readonly book: PdfBook; readonly chapters: readonly { readonly id: string; readonly snapshot: null }[]; readonly kept: false } | null> {
    const shared = await this.exportBook(invite, untitled);
    return shared === null ? null : { book: shared.book, chapters: shared.ids.map((id) => ({ id, snapshot: null })), kept: false };
  }

  /** NV: a note posted (or, with `edit`, said anew) on `invite`; the claims follow. */
  async post(invite: string, draft: Draft, edit: string | null = null): Promise<NoteOutcome> {
    return this.noting((shares) => shares.post(invite, draft, edit));
  }

  /** Decision 76: one of the invitee's own notes deleted; the claims follow. */
  async remove(invite: string, id: string): Promise<NoteOutcome> {
    return this.noting((shares) => shares.remove(invite, id));
  }

  private async noting(act: (shares: Shares) => Promise<NoteOutcome>): Promise<NoteOutcome> {
    const account = this.who();
    if (account === null) return { kind: 'failed' };
    const shares = await this.sharesFor(account);
    const outcome = await act(shares);
    await this.show(shares, await shares.claims());
    return outcome;
  }

  /** CC10, GP4: `account` logging out: its keyring and shared books dropped from this device. */
  async loggedOut(account: AccountId): Promise<void> {
    await this.forget(account);
  }

  /**
   * LO3, LO4 (another tab's logout, or another account logged in there): told of every change of the account's state;
   * once the account whose books this page holds is no longer the logged-in one (and not merely signed out by the relay,
   * C8, its copies kept for reading offline), they go as at a logout.
   */
  async accountChanged(): Promise<void> {
    try {
      await this.syncPending();
      await this.settle();
    } catch {
      // Deep-fix r5: run with nobody waiting (every change of the account's state): a storage refusal leaves the page as
      // it was; the next change or start applies table AS again.
    }
  }

  /** PW1–PW3: the invite's state as the store says now (another tab may have claimed, kept or put one away). */
  private async syncPending(): Promise<void> {
    const waiting = (await (await this.opened()).pending()) !== null;
    const state = this.inviteSignal[0]();
    if (!waiting && state.kind === 'waiting') this.inviteSignal[1]({ kind: 'none' });
    if (waiting && state.kind === 'none') this.inviteSignal[1]({ kind: 'waiting' });
  }

  /** LO1–LO6: `account`'s shares closed, every write under way waited out, then its keyring and books cleared here. */
  private async forget(account: string): Promise<void> {
    const held = this.shares;
    if (held?.account === account) {
      this.shares = null;
      await held.shares.close();
    }
    await (await this.opened()).clear(account);
    batch(() => {
      this.claimsSignal[1]([]);
      this.summariesSignal[1](new Map());
      this.unavailableSignal[1]([]);
    });
  }

  /** KG9 (`KeyringAccess`): the keyring's wrap for a change of `account`'s password. */
  async rewrap(account: AccountId, next: Typed, previous: Typed | null): Promise<{ readonly id: string; readonly wrapped: string } | null | 'failed'> {
    return (await this.sharesFor(account)).rewrap(next, previous);
  }
}

/** CL2–CL8: the relay's words an Accept can end in, as the card words them (`invite.error.<word>` in the catalogue). */
export const ACCEPT_WORDS: readonly RelayError[] = ['invite-address', 'invite-claimed', 'invite-ended', 'invite-gone', 'change-pending', 'grant-invalid', 'keyring-exists'];
